import { type Change, cardChanges, fieldOf } from "./draft-changes";
import type { PostDraft, PostPosition, PostShift, RowKey, RowMark } from "./model";
import type { NewShiftDefaults } from "./useNewShiftDefaults";

// D3 (signed 2026-10-17): the chat's change to a value someone set is marked on its row with what the row held before.

/** What the page fills in by itself; the chat replacing one of these is a first fill, not an overwrite. */
export type PageDefaults = Partial<NewShiftDefaults> & {
  /** A saved role's usual wage. */
  wageOf?: (role: PostPosition) => number | null | undefined;
};

const keyOf = ({ card, row, role, what }: Change) => (role ? `${card}/positions/${role}` : `${card}/${row}/${what}`);

/** A page default is told from a value someone set by value: a deliberate pick equal to the default is the default. */
const isDefault = (change: Change, shift: PostShift, defaults: PageDefaults) => {
  const role = shift.positions.find(({ key }) => key === change.role);
  const field = fieldOf(change.key);
  switch (field) {
    case "count":
      return role?.count === 1;
    case "pay":
      return !!role && role.pay_rate === defaults.wageOf?.(role);
    case "where":
      return change.what === "address" && shift.address_id === defaults.address_id;
    case "parking":
      return shift.parking_expense_type === defaults.parking_expense_type;
    case "arrival":
      return (shift.parking_instructions?.trim() || null) === (defaults.parking_instructions ?? null);
    case "manager":
      return shift.manager?.id === defaults.manager?.id;
    default:
      return false;
  }
};

/** The changes from one draft to the next that a row shows and that overwrote a value: the ones a mark can name. */
const overwrites = (before: PostDraft, after: PostDraft, engageOn: boolean) =>
  after.shifts.flatMap((shift) => {
    const was = before.shifts.find(({ key }) => key === shift.key);
    return was
      ? cardChanges(was, shift, engageOn).flatMap((change) =>
          change.row && change.was !== null ? [{ change, was }] : [],
        )
      : [];
  });

/**
 * The draft after a chat edit, with a mark on each row whose set value it overwrote. A row already marked keeps the value
 * from before the chat's first change.
 */
export const withChatMarks = (
  before: PostDraft,
  after: PostDraft,
  defaults: PageDefaults,
  engageOn: boolean,
): PostDraft => {
  const marks = { ...after.marks };
  let marked = false;
  for (const { change, was } of overwrites(before, after, engageOn)) {
    if (isDefault(change, was, defaults)) continue;
    const key = keyOf(change);
    const held = marks[key]?.kind === "changed" ? marks[key] : undefined;
    marks[key] = {
      kind: "changed",
      name: change.name,
      was: { ...held?.was, [change.what]: held?.was[change.what] ?? change.was ?? "" },
    };
    marked = true;
  }
  return marked ? { ...after, marks } : after;
};

/** After the chat's undo: each row it took back reads "Qwick put … back", with the value the undo took away. */
export const withPutBackMarks = (before: PostDraft, after: PostDraft, engageOn: boolean): PostDraft => {
  const marks = { ...after.marks };
  let marked = false;
  for (const { change } of overwrites(before, after, engageOn)) {
    const key = keyOf(change);
    const held = marks[key]?.kind === "put_back" ? marks[key] : undefined;
    marks[key] = { kind: "put_back", name: change.name, was: { ...held?.was, [change.what]: change.was ?? "" } };
    marked = true;
  }
  return marked ? { ...after, marks } : after;
};

/** The draft with only the marks `keep` keeps: the same draft when it keeps them all. */
const keepingMarks = (draft: PostDraft, keep: (key: string) => boolean): PostDraft => {
  if (!draft.marks) return draft;
  const entries = Object.entries(draft.marks);
  const kept = entries.filter(([key]) => keep(key));
  return kept.length === entries.length ? draft : { ...draft, marks: Object.fromEntries(kept) };
};

/** A hand edit clears the marks of each row it changed (of a role, that role's): the values are the manager's now. */
export const withoutHandMarks = (before: PostDraft, after: PostDraft): PostDraft => {
  if (!after.marks) return after;
  const changed = after.shifts.flatMap((shift) => {
    const was = before.shifts.find(({ key }) => key === shift.key);
    if (!was) return [];
    // Keys only: the audience's words never decide which marks go.
    return cardChanges(was, shift, true).flatMap((change) =>
      change.row ? [change.role ? keyOf(change) : `${change.card}/${change.row}/`] : [],
    );
  });
  return keepingMarks(
    after,
    (key) => !changed.some((each) => key === each || (each.endsWith("/") && key.startsWith(each))),
  );
};

/**
 * A role that posted keeps no mark, nor do its card's day, time, place and manager, which went out with it: they are the
 * live gig's now. A role of the card that has not posted keeps its own.
 */
export const withoutPostedMarks = (draft: PostDraft, entry: string): PostDraft => {
  const [card, role] = entry.split("/");
  const roleKey = `${card}/positions/`;
  return keepingMarks(
    draft,
    (key) => !key.startsWith(`${card}/`) || (key.startsWith(roleKey) && key !== `${roleKey}${role}`),
  );
};

/** A card's marks still on its rows, by their keys: a role no longer on it takes its mark with it. */
const keyedMarksOf = (draft: PostDraft, shift: PostShift, row?: RowKey) =>
  Object.entries(draft.marks ?? {}).filter(([key]) => {
    const [card, at, role] = key.split("/");
    if (card !== shift.key || (row && at !== row)) return false;
    return at !== "positions" || shift.positions.some(({ key: each }) => each === role);
  });

const marksOf = (draft: PostDraft, shift: PostShift, row?: RowKey) =>
  keyedMarksOf(draft, shift, row).map(([, mark]) => mark);

const markWords = ({ kind, name, was }: RowMark) => {
  const who = kind === "changed" ? `Qwick changed ${name}` : `Qwick put ${name} back`;
  return `${who} · was ${Object.values(was).join(", ")}`;
};

/** One mark's line on its row, keyed by what it marks: two marks can say the same words. */
export type MarkLine = { key: string; words: string };

/** "Qwick changed Bartender · was 2 pros, $22.00/hr": a row's marks, one line each; null for a row with none. */
export const rowMarks = (draft: PostDraft, shift: PostShift, row: RowKey): Array<MarkLine> | null => {
  const lines = keyedMarksOf(draft, shift, row).map(([key, mark]) => ({ key, words: markWords(mark) }));
  return lines.length > 0 ? lines : null;
};

const list = new Intl.ListFormat("en-US", { type: "conjunction" });

/** Review's one quiet line for a card the chat changed: "Qwick changed Bartender: count and pay". */
export const reviewMarkLine = (draft: PostDraft, shift: PostShift) => {
  const marks = marksOf(draft, shift);
  if (marks.length === 0) return null;
  // As the row says it: what the chat changed, or what its undo put back.
  const said = marks.map(({ kind, name, was }) => {
    if (kind === "put_back") return `put ${name} back`;
    const whats = Object.keys(was).filter((what) => !name.endsWith(what));
    return whats.length > 0 ? `changed ${name}: ${list.format(whats)}` : `changed ${name}`;
  });
  return `Qwick ${said.join("; ")}`;
};
